import React, { useMemo, useState } from 'react';
import { BookOpen, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

const tokens = (s) => (s || '').toLowerCase().match(/[a-z0-9]{3,}/g) || [];

/** Pick a paragraph from a drafted narrative section and drop it into an annex row answer. */
export function NarrativePickerDialog({ open, onOpenChange, project, target, onInsert }) {
  const [q, setQ] = useState('');
  const paras = useMemo(() => {
    const out = [];
    (project?.deliverables || []).filter(d => d.type === 'narrative' || d.type === 'generated').forEach(d => {
      (d.sections || []).forEach(s => {
        (s.draft || '').split(/\n{2,}/).map(x => x.trim()).filter(x => x.length > 40 && !x.startsWith('[GAP')).forEach((text, i) => {
          out.push({ key: `${d.id}-${s.heading}-${i}`, deliverable: d.title, heading: s.heading, text });
        });
      });
    });
    return out;
  }, [project]);
  const needle = q.trim() ? tokens(q) : tokens(target?.question);
  const ranked = useMemo(() => {
    const scored = paras.map(p => {
      const pt = new Set(tokens(`${p.heading} ${p.text}`));
      const hits = needle.filter(t => pt.has(t)).length;
      return { ...p, score: hits };
    });
    const filtered = q.trim() ? scored.filter(p => p.score > 0 || p.text.toLowerCase().includes(q.toLowerCase())) : scored;
    return filtered.sort((a, b) => b.score - a.score).slice(0, 60);
  }, [paras, needle, q]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="rfp-narrative-picker">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#4ABFCF]" />Answer from a narrative section</DialogTitle>
        </DialogHeader>
        {target?.question && <p className="text-[11px] text-slate-500 bg-slate-50 rounded p-2" data-testid="rfp-narrative-picker-question">Row: {target.question}</p>}
        <div className="relative"><Search className="w-3 h-3 absolute left-2 top-2.5 text-slate-400" /><Input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search drafted paragraphs… (ranked by overlap with the row text by default)" className="h-8 text-xs pl-7" data-testid="rfp-narrative-picker-search" /></div>
        {paras.length === 0 && <p className="text-xs text-slate-400 py-6 text-center" data-testid="rfp-narrative-picker-empty">No drafted narrative paragraphs yet — draft sections on the Narratives tab first.</p>}
        <div className="divide-y divide-slate-100 border border-slate-100 rounded-md">
          {ranked.map(p => (
            <div key={p.key} className="p-2.5 space-y-1 hover:bg-slate-50 group" data-testid={`rfp-narrative-para-${p.key.replace(/\W+/g, '_').slice(0, 60)}`}>
              <div className="flex items-center gap-2 text-[10px] text-slate-500">
                <Badge variant="outline" className="text-[9px]">{p.deliverable}</Badge><span className="truncate">{p.heading}</span>
                {p.score > 0 && <span className="text-[#4ABFCF]">{p.score} term{p.score === 1 ? '' : 's'} in common</span>}
                <span className="ml-auto flex items-center gap-1.5 opacity-60 group-hover:opacity-100">
                  <button type="button" onClick={() => onInsert(p.text, 'replace')} className="h-6 px-2 rounded bg-[#0F2040] text-white text-[10px]" data-testid="rfp-narrative-para-replace">Use as answer</button>
                  <button type="button" onClick={() => onInsert(p.text, 'append')} className="h-6 px-2 rounded border border-slate-200 text-[10px] text-slate-600" data-testid="rfp-narrative-para-append">Append</button>
                </span>
              </div>
              <p className="text-xs text-slate-700 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
